import React, { useEffect, useState, useMemo } from 'react';
import seed from '../data/wizard_seed.json';
import { API_BASE_URL } from '../config';

export default function CueWizard() {
  return (
    <div className="console-container">
      <div className="console-left" style={{ width: '100%' }}>
        <CueForm/>
        <WizardRecent />
      </div>
    </div>
  );
}

// CrosswordForm removed (unused)

function CueForm() {
  const [type, setType] = useState('praise');
  const [prompt, setPrompt] = useState('');
  const [target, setTarget] = useState('');
  const [msg, setMsg] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  // Preselect from hash query (e.g., #wizard?type=praise&target=Lady%20Macbeth&prompt=...)
  React.useEffect(() => {
    try {
      const hash = window.location.hash || '';
      const parts = hash.split('?');
      if (parts.length > 1) {
        const params = new URLSearchParams(parts[1]);
        const t = (params.get('type') || '').trim().toLowerCase();
        const tg = (params.get('target') || '').trim();
        const pr = (params.get('prompt') || '').trim();
        if (t && ['praise','solo','duel','rewrite','roast'].includes(t)) setType(t);
        if (tg) setTarget(tg);
        if (pr) setPrompt(pr);
      }
    } catch {}
  }, []);
  const submit = async () => {
    if (inFlight) return;
    setMsg('');
    setInFlight(true);
    try {
      // Normalize praise prompts to avoid redundant "Praise <target> ..." phrasing
      let cleanPrompt = (prompt || '').trim();
      if (type === 'praise') {
        // Strip any leading verb like "praise" and any occurrence of the target name
        cleanPrompt = cleanPrompt.replace(/^\s*praise\s+/i, '');
        if (target) {
          const t = String(target).trim().replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
          // remove leading target prefix and any standalone repeats
          // eslint-disable-next-line no-useless-escape
          cleanPrompt = cleanPrompt.replace(new RegExp(`^\s*${t}\s*[:,\-]?\s*`, 'i'), '');
          cleanPrompt = cleanPrompt.replace(new RegExp(`\b${t}\b`, 'ig'), '').trim();
        }
        // Encourage concise single sentence with a concrete merit if user didn't include constraints
        const needsConstraint = !/one\s+sentence/i.test(cleanPrompt);
        if (needsConstraint) {
          cleanPrompt = (cleanPrompt ? `${cleanPrompt}; ` : '') + 'one concrete merit; one sentence.';
        }
      }
      // Propose-only: create a draft entry for admin approval (with simple retry)
      const r = await fetchWithRetry(`${API_BASE_URL}/api/admin/cue/propose`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ type, target, prompt: cleanPrompt })}, 0);
      if (!r.ok) { setMsg('❌ Proposal failed.'); setShowInvite(false); return; }
      const dj = await r.json();
      if (!dj || !dj.ok || !dj.draft) { setMsg('❌ Proposal failed.'); setShowInvite(false); return; }
      const sid = dj.draft.scene_id || 'draft';
      setMsg(`✅ Proposed ${type} scene (draft ${sid}).`);
      setShowInvite(true);
      // Nudge WizardRecent to refresh sooner
      try { window.dispatchEvent(new CustomEvent('wizard-refresh')); } catch {}
    } catch (e) { setMsg(`Error: ${e}`); }
    finally { setInFlight(false); }
  };
  return (
    <div className="console-panel">
      <div className="line" style={{ marginBottom: 8, opacity: .9 }}>
        Issue a scene cue (praise, solo, duel, rewrite, roast). Admin will review and may post to <a href="https://bsky.app/profile/bitbardofficial.bsky.social" target="_blank" rel="noreferrer">Bluesky</a>.
      </div>
      {showInvite && (
        <div className="line" style={{ marginBottom: 8, color:'#9bdcff' }}>
          Your scene is live on Discord. Join here: <a href="https://discord.gg/3Mju8yEHPD" target="_blank" rel="noreferrer">invite link</a>.
        </div>
      )}
      <div className="line">
        <select value={type} onChange={e=>setType(e.target.value)}>
          <option value="praise">Praise</option>
          <option value="solo">Solo</option>
          <option value="duel">Duel</option>
          <option value="rewrite">Rewrite</option>
          <option value="roast">Roast</option>
        </select>
      </div>
      {(type==='praise' || type==='roast') && (
        <div className="line">
          <select value={target} onChange={e=>setTarget(e.target.value)}>
            <option value="">Select target (optional)</option>
            <option value="Falstaff">Falstaff</option>
            <option value="Lady Macbeth">Lady Macbeth</option>
            <option value="Iago">Iago</option>
            <option value="Miranda">Miranda</option>
            <option value="Sir Andrew Aguecheek">Sir Andrew Aguecheek</option>
          </select>
        </div>
      )}
      <div className="line"><textarea placeholder="Optional prompt/context" value={prompt} onChange={e=>setPrompt(e.target.value)} rows={4} style={{ width:'80%' }}/></div>
      <div className="line"><button onClick={submit} className="btn-dark" disabled={inFlight}>{inFlight ? 'Issuing…' : 'Issue Cue'}</button></div>
      <div className="line" style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
        Certain cues are off the playbill—Bitbard’s choice.
      </div>
      {msg && <div className="line" style={{ color: msg.startsWith('✅')?'#7bd88f':'#ff6b6b' }}>{msg}</div>}
    </div>
  );
}

function WizardRecent() {
  const [recent, setRecent] = useState(() => (Array.isArray(seed?.items) ? seed.items : []));
  const [avatars, setAvatars] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [showFull, setShowFull] = useState({}); // scene_id -> bool

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [r1, r2] = await Promise.all([
          fetchWithRetry(`${API_BASE_URL}/api/wizard/recent_db?limit=3`, { method:'GET' }, 2),
          fetchWithRetry(`${API_BASE_URL}/api/ui/chat/avatars`, { method:'GET' }, 2)
        ]);
        if (!r1.ok) throw new Error(`recent ${r1.status}`);
        if (!r2.ok) throw new Error(`avatars ${r2.status}`);
        const j1 = await r1.json();
        const j2 = await r2.json();
        if (!mounted) return;
        setRecent(Array.isArray(j1.recent) ? j1.recent : []);
        setAvatars(j2 || {});
      } catch (e) {
        if (!mounted) return;
        // Keep seed on screen if fetch fails
        setError(`Load failed: ${e}`);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    const t = setInterval(load, 30_000);
    return () => { mounted = false; clearInterval(t); };
  }, [refreshTick]);

  useEffect(() => {
    const onRefresh = () => setRefreshTick(t => t + 1);
    window.addEventListener('wizard-refresh', onRefresh);
    return () => window.removeEventListener('wizard-refresh', onRefresh);
  }, []);

  const items = useMemo(() => {
    return (recent || []).slice(0,3).map(sc => ({
      id: sc.scene_id,
      posts: (sc.posts || []).map(p => ({
        author: p.author || '',
        content: p.content || '',
        timestamp: p.timestamp || ''
      }))
    }));
  }, [recent]);

  const avatarFor = (name='?') => {
    const n = String(name || '?').trim();
    const key = n.toLowerCase();
    if (key.startsWith('bitbard')) return avatars['bitbard'] || avatars['BitBard'] || '';
    if (key.startsWith('lady macbeth')) return avatars['lady macbeth'] || avatars['Lady Macbeth'] || '';
    if (key.startsWith('sir andrew')) return avatars['sir andrew'] || avatars['Sir Andrew Aguecheek'] || '';
    if (key.startsWith('falstaff')) return avatars['falstaff'] || avatars['Falstaff'] || '';
    if (key.startsWith('miranda')) return avatars['miranda'] || avatars['Miranda'] || '';
    if (key.startsWith('iago')) return avatars['iago'] || avatars['Iago'] || '';
    return avatars['default'] || avatars['bitbard'] || '';
  };

  const utcString = (ts) => {
    try { return new Date(ts).toISOString().replace('T',' ').replace('Z',' UTC'); } catch { return ''; }
  };

  return (
    <div className="console-panel" style={{ marginTop: 16 }}>
      <div className="line" style={{ marginBottom: 8 }}>Recent Wizard Scenes (last 3)</div>
      {loading && <div className="line">Loading…</div>}
      {error && <div className="line" style={{ color:'#ff6b6b' }}>{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className="line">No recent scenes yet.</div>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="line" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))', gap: 16 }}>
          {items.map(scene => {
            const firstTs = scene.posts?.[0]?.timestamp || '';
            const shown = showFull[scene.id] ? scene.posts : scene.posts.slice(0, 4);
            return (
              <div key={scene.id} style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px', background: 'linear-gradient(180deg, #0f2436 0%, #0b1a29 100%)' }}>
                <div className="line" style={{ opacity: .8, fontSize: 12, marginBottom: 6 }}>{utcString(firstTs)}</div>
                <div>
                  {shown.map((p, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '6px 0' }}>
                      <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(p.author)})`, width: 28, height: 28, borderRadius: 20, backgroundSize: 'cover', backgroundPosition: 'center', flex: '0 0 auto' }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{p.author}</div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{p.content}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {(scene.posts.length > shown.length) && (
                  <div className="line" style={{ marginTop: 8 }}>
                    <button onClick={() => setShowFull(s => ({ ...s, [scene.id]: !s[scene.id] }))} style={{ fontSize: 12, padding: '4px 10px', background: 'linear-gradient(180deg, #173049 0%, #0f2538 100%)', border: '1px solid rgba(255,255,255,0.15)' }}>
                      {showFull[scene.id] ? 'Show less' : `Show full scene (${scene.posts.length} posts)`}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Lightweight fetch retry helper to smooth over transient 404/edge hiccups
async function fetchWithRetry(url, init = {}, retries = 3) {
  let lastErr;
  let delay = 250;
  for (let i = 0; i <= retries; i++) {
    try {
      const res = await fetch(url, init);
      if (!res.ok && (res.status === 404 || res.status === 502 || res.status === 504)) {
        // brief backoff then retry (exponential)
        const currentDelay = delay; // Capture current delay value for this iteration
        await new Promise(r => setTimeout(r, currentDelay));
        delay = Math.min(1200, delay * 2);
        lastErr = new Error(`HTTP ${res.status}`);
        continue;
      }
      return res;
    } catch (e) {
      lastErr = e;
      const currentDelay = delay; // Capture current delay value for this iteration
      await new Promise(r => setTimeout(r, currentDelay));
      delay = Math.min(1200, delay * 2);
    }
  }
  throw lastErr || new Error('fetch failed');
}
